import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import useDebounce from '../../hooks/useDebounce';
import {initialize, changeField, downloadDocsList, readCategoryList, readDocsList} from '../../modules/admin/docs';
import styles from './styles/docs-container.module.css';

import DocsLayout from '../../components/admin/docs-women/DocsLayout';
import ExpandSideBar from '../../components/layout/ExpandSideBar';

const findCategoryInfo = (currentId, readCategoryListResponse) => { // readCategoryList 중에서 currentId와 일치하는 id를 갖는 카테고리를 찾아 해당 카테고리 이름과 설명을 찾는 함수
    if (currentId === 0) {
        return {
            id: 0,
            categoryName: '모든 카테고리',
            categoryDescription: '',
            depth: 0
        };
    }

    if (!readCategoryListResponse) {
        return {
            id: 0,
            categoryName: '카테고리 조회 오류',
            categoryDescription: '카테고리 조회 오류',
            depth: 0
        };
    }
  
    let currentCategory = readCategoryListResponse.find( // 먼저, depth가 1인 카테고리(메인 카테고리) 순회
        category => category.id === currentId
    );
  
    if (!currentCategory) { // 메인 카테고리에서 찾지 못한 경우, 하위 카테고리에서 검색
        for (const mainCategory of readCategoryListResponse) {
            if (mainCategory.categoryList) {
                const subCategory = mainCategory.categoryList.find(
                    sub => sub.id === currentId
                );
                
                if (subCategory) {
                    currentCategory = subCategory;
                    break;
                }
            }
        }
    }
  
    if (currentCategory) {
        const { id, categoryName, categoryDescription, depth } = currentCategory;
        return {
            id,
            categoryName: categoryName || '',
            categoryDescription: categoryDescription || '',
            depth: depth || 0
        };
    }

    return {
        id: currentId,
        categoryName: '카테고리 이름',
        categoryDescription: '카테고리 설명',
        depth: 0
    };
};

export default function DocsContainer() {
    const dispatch = useDispatch();

    const { id } = useParams();

    const { currentId, categoryName, categoryDescription, depth, name, page, pagination, readCategoryListResponse, readCategoryListError, readCategoryListLoading, readDocsListResponse, readDocsListError, readDocsListLoading, downloadResponse } = useSelector(({ docs, loading }) => ({
        currentId: docs.currentId,
        categoryName: docs.categoryName,
        categoryDescription: docs.categoryDescription,
        depth: docs.depth,
        name: docs.name,
        page: docs.page,
        pagination: docs.pagination,
        readCategoryListResponse: docs.readCategoryListResponse,
        readCategoryListError: docs.readCategoryListError,
        readCategoryListLoading: loading['docs/READ_CATEGORY_LIST'],
        readDocsListResponse: docs.readDocsListResponse,
        readDocsListError: docs.readDocsListError,
        readDocsListLoading: loading['docs/READ_DOCS_LIST'],
        downloadResponse: docs.downloadResponse
    }));

    const debouncedName = useDebounce(name, 1000);

    const handleChange = (e) => {    
        const { name, value } = e.target;
        dispatch(changeField({ name, value }));
    };

    const changeCurrentId = (id) => {
        handleChange({
            target: {
                name: 'currentId',
                value: id
            }
        });
    };

    const hanldeDownload = (docsList) => {
        dispatch(downloadDocsList(docsList));
    };

    const reloadCategoryLsit = () => {
        dispatch(readCategoryList());
    };

    const reloadDocsLsit = () => {
        dispatch(readDocsList({
            id: currentId,
            name: null,
            page: 1
        }));
    };

    useEffect(() => {
        dispatch(initialize());
        dispatch(readCategoryList());
    }, []);

    useEffect(() => {
        dispatch(readDocsList({
            id: currentId,
            name: debouncedName ? debouncedName.trim() : null,
            page
        }));
    }, [currentId, debouncedName, page]);

    useEffect(() => {
        if (id) {
            const paramsId = parseInt(id, 10);
            dispatch(changeField({ name: 'currentId', value: paramsId || 0 }));
        }
    }, [id]);

    useEffect(() => {
        if (readCategoryListResponse) {
            const { id, categoryName, categoryDescription, depth } = findCategoryInfo(currentId, readCategoryListResponse);
            
            dispatch(changeField({ name: 'currentId', value: id }));
            dispatch(changeField({ name: 'categoryName', value: categoryName }));
            dispatch(changeField({ name: 'categoryDescription', value: categoryDescription }));
            dispatch(changeField({ name: 'depth', value: depth }));
            
            dispatch(changeField({ name: 'name', value: null }));
        }
        
        dispatch(changeField({ name: 'name', value: null }));
    }, [currentId, readCategoryListResponse]);

    return (
        <div className={styles.wrapper}>
            <ExpandSideBar
                currentId={currentId}
                response={readCategoryListResponse}
                error={readCategoryListError}
                loading={readCategoryListLoading}
                changeCurrentId={changeCurrentId}
                handleReload={reloadCategoryLsit}
            />
            <DocsLayout
                currentId={currentId}
                name={name}
                categoryName={categoryName}
                categoryDescription={categoryDescription}
                depth={depth}
                pagination={pagination}
                listResponse={readDocsListResponse}
                listError={readDocsListError}
                listLoading={readDocsListLoading}
                downloadResponse={downloadResponse}
                handleChange={handleChange}
                changeCurrentId={changeCurrentId}
                hanldeDownload={hanldeDownload}
                handleReload={reloadDocsLsit}
            />
        </div>
    );
}